import { groupBy, min, uniq } from "lodash";
import type { ProductResponse } from "../../admin/products/productsApi";
import { useProducts } from "../../admin/products/useProducts";
import type { ProviderListResponseItem } from "../../admin/providers/providersApi";
import { useProviders } from "../../admin/providers/useProviders";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { EmailTemplate, EmailTemplateEntry } from "../../misc/emailTemplates";
import type { SavedOrder, SavedOrderProduct } from "../orderTypes";

interface Provider {
  readonly id: string;
  readonly name: string;
  readonly contact: {
    readonly email: string | undefined;
  };
  readonly contractNumber: string | undefined;
  readonly hasEmailRecipients: boolean;
}

interface ProviderEmails {
  readonly providerName: string;
  readonly emails: EmailTemplateEntry[];
}

interface EmailTemplateProductOption {
  readonly id: string;
  readonly quantity: number;
  readonly name: string;
}

interface EmailTemplateProduct {
  readonly id: string;
  readonly name: string;
  readonly date: string;
  readonly time?: string;
  readonly bookingNumber?: string;
  readonly contactEmail: string | undefined;
  readonly options: EmailTemplateProductOption[];
}

interface ProductsForProvider {
  readonly products: EmailTemplateProduct[];
  readonly provider: Provider;
}

export type EmailTemplateOrder = Pick<SavedOrder, "id" | "userFriendlyId" | "name"> & { products: { id: string }[] };

export const useProviderEmailTemplates = (order: SavedOrder): { isLoading: boolean; templates: ProviderEmails[] } => {
  const { providers, isLoading: areProvidersLoading } = useProviders();
  const { products, isLoading: areProductsLoading } = useProducts();
  const productsWithProvider = order.products
    .filter((p) => !p.cancelled)
    .map((product) => {
      const provider = getProviderForProduct(product, providers);
      const matchingProduct = products.find((p) => p.id === product.id);
      return {
        product,
        provider: provider
          ? { ...provider, hasEmailRecipients: matchingProduct?.provider?.hasEmailRecipients ?? false }
          : undefined,
      };
    })
    .filter((v): v is typeof v & { provider: NonNullable<typeof v.provider> } => !!v.provider?.contact.email);
  const productsForProvider: ProductsForProvider[] = Object.entries(
    groupBy(productsWithProvider, (v) => v.provider.id),
  ).map(([, values]) => ({
    provider: values[0].provider,
    products: values.map(({ product }) => orderProductToEmailTemplateProduct(product, products)),
  }));
  const productsWithBookingsForProvider = productsForProvider
    .map(({ provider, products }) => ({
      provider,
      products: products.filter((p) => !!p.bookingNumber),
    }))
    .filter(({ products }) => products.length > 0);

  const emailsForProviders = groupBy(
    [
      ...capacityForGroupEmailTemplate(order, productsForProvider),
      ...newBookingEmailTemplates(order, productsForProvider),
      ...bookingChangeEmailTemplates(order, productsWithBookingsForProvider),
      ...bookingInvoiceEmailTemplates(order, productsWithBookingsForProvider),
      ...bookingCancellationEmailTemplates(order, productsWithBookingsForProvider),
    ],
    (v) => v.providerName,
  );
  return {
    isLoading: areProductsLoading || areProvidersLoading,
    templates: Object.entries(emailsForProviders).map(([providerName, emails]) => ({
      providerName,
      emails: emails.map(({ email }) => email),
    })),
  };
};

function orderProductToEmailTemplateProduct(
  product: SavedOrderProduct,
  products: ProductResponse[],
): EmailTemplateProduct {
  const matchingProduct = products.find((p) => p.id === product.id);
  return {
    id: product.id,
    name: matchingProduct?.name ?? "nd.",
    date: product.date,
    time: product.time,
    bookingNumber: product.bookingNumber,
    contactEmail: matchingProduct?.contact.email,
    options: product.options.map(({ id, quantity }) => {
      const matchingOption = matchingProduct?.options.find((o) => o.id === id);
      return {
        id,
        quantity,
        name: matchingOption?.providerDetails?.optionName ?? matchingOption?.name ?? "nd.",
      };
    }),
  };
}

function newBookingEmailTemplates(
  order: EmailTemplateOrder,
  productsForProvider: ProductsForProvider[],
): { providerName: string; email: EmailTemplateEntry }[] {
  return productsForProvider.map(({ provider, products }) => ({
    providerName: provider.name,
    email: {
      name: `Nowa rezerwacja: ${order.userFriendlyId}`,
      template: new NewBookingEmailTemplate(
        order,
        products,
        provider.contact.email ?? "",
        provider.contractNumber,
        provider.hasEmailRecipients,
      ),
    },
  }));
}

function capacityForGroupEmailTemplate(order: EmailTemplateOrder, productsForProvider: ProductsForProvider[]) {
  return productsForProvider.map(({ provider, products }) => ({
    providerName: provider.name,
    email: {
      name: "Sprawdź dostępność",
      template: new CapacityForGroupEmailTemplate(order, products, provider.contact.email ?? ""),
    },
  }));
}

// TODO: handle different bookings for single provider
function bookingChangeEmailTemplates(
  order: EmailTemplateOrder,
  productsForProvider: ProductsForProvider[],
): { providerName: string; email: EmailTemplateEntry }[] {
  return productsForProvider.map(({ provider, products }) => {
    const bookingNumbers = uniq(products.map((p) => p.bookingNumber).filter((b): b is string => !!b));
    return {
      providerName: provider.name,
      email: {
        name: `Zmiana rezerwacji: ${bookingNumbers.join(", ")}`,
        template: new BookingChangeEmailTemplate(order, bookingNumbers, products, provider.contact.email ?? ""),
      },
    };
  });
}

function bookingInvoiceEmailTemplates(
  order: EmailTemplateOrder,
  productsForProvider: ProductsForProvider[],
): { providerName: string; email: EmailTemplateEntry }[] {
  return productsForProvider.map(({ provider, products }) => {
    const bookingNumbers = uniq(products.map((p) => p.bookingNumber).filter((b): b is string => !!b));
    return {
      providerName: provider.name,
      email: {
        name: `Faktura: ${bookingNumbers.join(", ")}`,
        template: new InvoiceForBookingEmailTemplate(
          order,
          bookingNumbers,
          provider.contact.email ?? "",
          products.map((p) => p.contactEmail).filter((e): e is string => !!e),
        ),
      },
    };
  });
}

function bookingCancellationEmailTemplates(
  order: EmailTemplateOrder,
  productsForProvider: ProductsForProvider[],
): { providerName: string; email: EmailTemplateEntry }[] {
  return productsForProvider.map(({ provider, products }) => {
    const bookingNumbers = uniq(products.map((p) => p.bookingNumber).filter((b): b is string => !!b));
    return {
      providerName: provider.name,
      email: {
        name: `Anulacja rezerwacji: ${bookingNumbers.join(", ")}`,
        template: new BookingCancellationEmailTemplate(order, bookingNumbers, products, provider.contact.email ?? ""),
      },
    };
  });
}

function getProviderForProduct(
  orderProduct: SavedOrderProduct,
  providers: ProviderListResponseItem[],
): Omit<Provider, "hasEmailRecipients"> | undefined {
  const providerId = orderProduct?.providerId;
  if (!providerId) {
    return undefined;
  }
  const provider = providers.find((p) => p.id === providerId);
  if (!provider) {
    return undefined;
  }
  return {
    id: provider.id,
    name: provider.name,
    contact: { email: provider.contact?.email },
    contractNumber: provider.contractNumber,
  };
}

abstract class ProviderEmailTemplate implements EmailTemplate {
  protected constructor(
    readonly providerEmail: string,
    protected readonly productEmails: string[],
    readonly ccEmails?: string[],
  ) {}

  get emails(): string[] {
    return uniq([this.providerEmail, ...this.productEmails]);
  }

  abstract get subject(): string;

  abstract get body(): string;

  protected static formatDate(date: string) {
    return dayjs(date, ISO_DATE_FORMAT).format("DD.MM.YYYY");
  }
}

abstract class ProviderEmailWithProductsTemplate extends ProviderEmailTemplate {
  protected constructor(
    protected readonly products: EmailTemplateProduct[],
    providerEmail: string,
    ccEmails?: string[],
  ) {
    super(
      providerEmail,
      products.map((p) => p.contactEmail).filter((e): e is string => !!e),
      ccEmails,
    );
  }

  protected productDetails(product: EmailTemplateProduct): string {
    const productDate = ProviderEmailTemplate.formatDate(product.date);
    const productTime = product.time ? ` ${product.time}` : "";
    const options = product.options.map((o) => this.productOptionDetails(o)).join("\n");
    return `${productDate}${productTime} ${product.name}\n${options}`;
  }

  private productOptionDetails(option: EmailTemplateProductOption): string {
    return `  ${option.quantity} x ${option.name}`;
  }

  protected getDateOfFirstProduct(): string {
    const minDate = min(this.products.map((p) => p.date));
    return minDate ? ProviderEmailTemplate.formatDate(minDate) : "";
  }
}

class NewBookingEmailTemplate extends ProviderEmailWithProductsTemplate {
  constructor(
    private readonly order: EmailTemplateOrder,
    products: EmailTemplateProduct[],
    providerEmail: string,
    private readonly contractNumber: string | undefined,
    private readonly hasEmailRecipients: boolean,
  ) {
    super(products, providerEmail);
  }

  get subject() {
    return `New booking for group: ${this.order.userFriendlyId} for ${this.getDateOfFirstProduct()}`;
  }

  get body() {
    const contractNumberText = this.contractNumber ? `\n${this.contractNumber}\n` : "";
    const bookingPageUrl = this.hasEmailRecipients
      ? `\n${window.location.origin}/orders/${this.order.id}/bookings\n`
      : "";
    return `Dear Colleagues,
${contractNumberText}
Please book for our group no ${this.order.userFriendlyId} following services:

${this.products.map((p) => this.productDetails(p)).join("\n")}

Please confirm, and send us booking confirmation.
${bookingPageUrl}
Best regards,
`;
  }
}

class CapacityForGroupEmailTemplate extends ProviderEmailWithProductsTemplate {
  constructor(
    private readonly order: EmailTemplateOrder,
    products: EmailTemplateProduct[],
    providerEmail: string,
    ccEmails?: string[],
  ) {
    super(products, providerEmail, ccEmails);
  }

  get subject() {
    return `Capacity for a group: ${this.order.userFriendlyId} for ${this.getDateOfFirstProduct()}`;
  }

  get body() {
    return `Dear Colleagues,
I’d like to ask for capacity for a group as follows:

${this.products.map((p) => this.productDetails(p)).join("\n")}

Please let me know how the situation looks like but for now don’t make any booking.

Best regards,
`;
  }
}

class BookingChangeEmailTemplate extends ProviderEmailWithProductsTemplate {
  constructor(
    private readonly order: EmailTemplateOrder,
    private readonly bookingNumbers: string[],
    products: EmailTemplateProduct[],
    providerEmail: string,
  ) {
    super(products, providerEmail);
  }

  get subject() {
    return `Booking${this.bookingNumbers.length > 1 ? "s" : ""} ${this.bookingNumbers.join(", ")} for group ${this.order.userFriendlyId} for ${this.getDateOfFirstProduct()} to change`;
  }

  get body() {
    return `Dear Colleagues,

Please change our booking${this.bookingNumbers.length > 1 ? "s" : ""} no ${this.bookingNumbers.join(", ")} (for group no ${this.order.userFriendlyId}) as follows:

${this.products.map((p) => this.productDetails(p)).join("\n")}

Please confirm the changes and send us a booking confirmation.

Best regards,
`;
  }
}

export class InvoiceForBookingEmailTemplate extends ProviderEmailTemplate {
  constructor(
    private readonly order: Pick<EmailTemplateOrder, "name" | "userFriendlyId">,
    private readonly bookingNumbers: string[],
    providerEmail: string,
    productEmails: string[],
  ) {
    super(providerEmail, productEmails);
  }

  get subject() {
    return `Invoice for booking${this.bookingNumbers.length > 1 ? "s" : ""}: ${this.bookingNumbers.join(", ")} for group no ${this.order.userFriendlyId}`;
  }

  get body() {
    return `Dear Colleagues,

Please send us the final invoice for ${this.bookingNumbers.length > 1 ? "bookings" : "the booking"} ${this.bookingNumbers.join(", ")}.
Our group name: ${this.order.userFriendlyId} ${this.order.name}.

Best regards,
`;
  }
}

class BookingCancellationEmailTemplate extends ProviderEmailWithProductsTemplate {
  constructor(
    private readonly order: EmailTemplateOrder,
    private readonly bookingNumbers: string[],
    products: EmailTemplateProduct[],
    providerEmail: string,
  ) {
    super(products, providerEmail);
  }

  get subject() {
    return `Booking${this.bookingNumbers.length > 1 ? "s" : ""} no: ${this.bookingNumbers.join(", ")} for group no ${this.order.userFriendlyId} to cancel`;
  }

  get body() {
    return `Dear Colleagues,

Please cancel ${this.bookingNumbers.length > 1 ? "bookings" : "the booking"} ${this.bookingNumbers.join(", ")}
for the group ${this.order.userFriendlyId}.
${this.orderProductRows}

Please confirm the cancellation.

Best regards,
`;
  }

  private get orderProductRows() {
    return this.products
      .map((p) => {
        const date = dayjs(p.date, ISO_DATE_FORMAT).format("DD.MM.YYYY");
        const time = p.time ? ` ${p.time}` : "";
        return `${date}${time} ${p.name}`;
      })
      .join("\n");
  }
}
